import { css } from '@emotion/react'
import {
    CSSProperties,
    memo,
    MouseEventHandler,
    ReactEventHandler,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    forceSnapUpdate,
    setStaticImage,
    SnapSize,
} from '../../../state/state.timers'
import { useIsomorphicLayoutEffect } from '../../Hooks/useIsomorphicLayoutEffect'
import { useSnaps } from '../../Hooks/useSnaps'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import { greenMFC, PAPER_HEIGHTS } from '../../theme'
import LoungePoster from './LoungePoster'
import NotAvailablePoster from './NotAvailablePoster'
import Poster from './Poster'
import PosterImage from './PosterImage'

const rootStyles = css({
    display: 'flex',
    background: palette.backgroundLightGray,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    transform: 'translate3d(0,0,0)',
    overflow: 'hidden',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    aspectRatio: '16/9',
})
const loadingStyles = css({
    position: 'absolute',
    opacity: 0.92,
    color: greenMFC,
    padding: getThemeSpacing(3),
    textAlign: 'center',
    fontSize: '0.8125rem',
    fontWeight: 'bold',
    lineHeight: '1.125rem',
    zIndex: PAPER_HEIGHTS.base,
})
const extendedStyles = css({
    fontSize: 24,
    lineHeight: '24px',
})

export interface PreviewPosterProps {
    id: number
    banned: boolean
    channel: number
    compact?: boolean
    extended?: boolean
    height?: number
    isAway: boolean
    isBroadcasting: boolean
    isClubShow: boolean
    isGroupShow: boolean
    isLounge?: boolean
    isModel: boolean
    isNGVideo: boolean
    isOffline?: boolean
    isLoadingPrivateShow?: boolean
    isPrivateShow: boolean
    isTruePrivate: boolean
    isAnonymous?: boolean
    large?: boolean
    noBackground?: boolean
    small?: boolean
    onClick?: MouseEventHandler<HTMLDivElement>
    onLoad?: ReactEventHandler<HTMLImageElement>
    onMouseLeave?: MouseEventHandler<HTMLDivElement>
    phase: string
    rounded?: boolean
    server: string
    setDimensions?: boolean
    showRecommendations?: boolean
    ssr?: boolean
    staticImage?: boolean
    width?: number
}

const PreviewPoster = memo((props: PreviewPosterProps) => {
    const {
        id,
        banned,
        compact,
        extended,
        height,
        isAway,
        isBroadcasting,
        isClubShow,
        isGroupShow,
        isLounge,
        isModel,
        isOffline,
        isLoadingPrivateShow,
        isPrivateShow,
        isTruePrivate,
        isAnonymous,
        large = false,
        small = false,
        noBackground,
        onClick = () => {},
        onLoad = () => {},
        onMouseLeave = () => {},
        rounded,
        setDimensions = true,
        showRecommendations,
        ssr,
        staticImage = false,
        width,
    } = props

    const src = useSnaps(
        id,
        large ? SnapSize.large : small ? SnapSize.small : SnapSize.medium,
    )
    const ref = useRef<HTMLImageElement>(null)
    const errorCounter = useRef<number>(0)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (staticImage) setStaticImage(id, staticImage)
        if (!staticImage) forceSnapUpdate(id)
    }, [id, staticImage])

    const handleOnLoad = useCallback(
        (e?: SyntheticEvent<HTMLImageElement>) => {
            setLoaded(true)
            errorCounter.current = 0
            onLoad?.(e)
        },
        [onLoad],
    )

    const handleOnError = useCallback(() => {
        console.log('Image errored!!!')
        if (errorCounter.current > 3) return
        errorCounter.current++
        if (!staticImage) {
            forceSnapUpdate(id)
        }
    }, [id, staticImage])

    useIsomorphicLayoutEffect(() => {
        const img = ref.current
        // image may be loaded before React registers the onLoad event (SSR)
        if (ssr && img && img.complete && !loaded) {
            handleOnLoad({ target: img } as any)
        }
    }, [])

    const inHiddenShow =
        isTruePrivate || isPrivateShow || isGroupShow || isClubShow
    const showPoster =
        banned ||
        isAway ||
        !isModel ||
        isOffline ||
        inHiddenShow ||
        !isBroadcasting

    const showType = (() => {
        if (isGroupShow) return 'GROUP'
        if (isTruePrivate) return 'TRUE PRIVATE'
        if (isPrivateShow) return 'PRIVATE'
        if (isClubShow) return 'CLUB'
    })()

    const details = (() => {
        if (isGroupShow) return 'Click on the "Group" button to join the show'
        if (isTruePrivate) return ''
        if (isPrivateShow) return 'Click on the "Spy" button to watch the show'
        if (isClubShow)
            return 'Club members can join the show from the message displayed in the chat'
    })()

    const memberStatusText =
        (!compact ? 'MEMBER IS ' : '') + (isOffline ? 'OFFLINE' : 'ONLINE')

    const bannedNotice = (!compact ? 'ROOM ' : '') + 'NOT AUTHORIZED'

    const hiddenShowNotice = useMemo(() => {
        if (isLoadingPrivateShow) {
            if (compact) return 'LOADING PRIVATE'
            if (!extended) return `LOADING ${showType} SHOW`
            const text = `${showType} SHOW IS LOADING...`
            return <NotAvailablePoster showText={text} />
        }

        if (compact) return showType + (isTruePrivate ? '' : ' SHOW')
        if (!extended) return `MODEL IS IN A ${showType} SHOW`
        const text = `MODEL IS CURRENTLY IN A ${showType} SHOW`
        return <NotAvailablePoster showText={text} details={details} />
    }, [compact, extended, isTruePrivate, showType, details])

    const awayNotice = useMemo(() => {
        if (compact) return 'AWAY'
        if (!extended) return 'MODEL IS AWAY'
        const text = 'MODEL IS CURRENTLY AWAY, BUT WILL RETURN SHORTLY'
        return <span css={extendedStyles}>{text}</span>
    }, [compact, extended])

    const offlineNotice = useMemo(() => {
        if (compact) return 'OFFLINE'
        if (!extended) return 'MODEL IS OFFLINE'
        return <span css={extendedStyles}>MODEL IS OFFLINE</span>
    }, [compact, extended])

    const notBroadcastingNotice = useMemo(() => {
        if (compact) return 'NOT STREAMING'
        if (!extended) return 'MODEL NOT BROADCASTING'
        return <span css={extendedStyles}>MODEL IS NOT BROADCASTING</span>
    }, [compact, extended])

    const posterText = useMemo(() => {
        if (isAnonymous) return 'MODEL IS ANONYMOUS'
        if (!isModel) return memberStatusText
        if (banned) return bannedNotice
        if (inHiddenShow) return hiddenShowNotice
        if (isAway) return awayNotice
        if (isOffline) return offlineNotice
        if (!isBroadcasting) return notBroadcastingNotice
    }, [
        isAway,
        banned,
        isModel,
        isOffline,
        inHiddenShow,
        isBroadcasting,
        hiddenShowNotice,
        notBroadcastingNotice,
        offlineNotice,
        awayNotice,
        isAnonymous,
    ])

    const style = useMemo(() => {
        const s: CSSProperties = {
            ...(height > 0 ? { height, maxHeight: height } : null),
            ...(!showRecommendations && width > 0
                ? { width, maxWidth: width }
                : null),
            ...(showRecommendations && width > 0
                ? { width: '100%', maxWidth: '100%' }
                : null),
            ...(rounded && { borderRadius: shape.borderRadius }),
            // ...(noBackground ? { background: 'transparent' } : null),
        }
        return s
    }, [height, width, rounded, noBackground])

    const { Component, noStatus } = useMemo(() => {
        if (isLounge) {
            return {
                Component: <LoungePoster width={width} height={height} />,
                noStatus: false,
            }
        }
        if (showPoster) {
            return {
                Component: (
                    <Poster
                        compact={compact}
                        hiddenShow={inHiddenShow}
                        isOffline={isOffline}
                        modelId={id}
                        showRecommendations={showRecommendations}
                    >
                        {posterText}
                    </Poster>
                ),
                noStatus: false,
            }
        }
        return {
            Component: (
                <PosterImage
                    height={height}
                    width={width}
                    src={src}
                    ref={ref}
                    onLoad={handleOnLoad}
                    onError={handleOnError}
                    testId="Image__BroadcastPreview"
                    setDimensions={setDimensions}
                />
            ),
            noStatus: true,
        }
    }, [
        id,
        isOffline,
        src,
        width,
        height,
        compact,
        isLounge,
        posterText,
        showPoster,
        inHiddenShow,
        setDimensions,
        handleOnError,
        handleOnLoad,
    ])

    const loading = noStatus && isBroadcasting && !large && !loaded

    if (noStatus && !src) {
        return null
    }

    return (
        <div
            css={rootStyles}
            style={style}
            onClick={onClick}
            onMouseLeave={onMouseLeave}
            data-testid="PreviewPosterImage__Image"
            id={`snap-video-${id}`}
        >
            {Component}
            {loading && (
                <div css={loadingStyles} data-testid="Image__ModelStatus">
                    LOADING...
                </div>
            )}
        </div>
    )
})

export default PreviewPoster
